// ============================================
// Mention Autocomplete Component
// Dropdown for @mentioning users in chat
// ============================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL, getAnyValidToken } from '../../utils/authUtils';

/**
 * Autocomplete dropdown for @mentions
 * Triggered when user types "@" in message input
 */
const MentionAutocomplete = ({
  channelId,
  searchQuery,
  position,
  onSelect,
  onClose,
  visible
}) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef(null);

  // Fetch channel members
  const fetchMembers = useCallback(async (query) => {
    if (!channelId) return;

    const token = getAnyValidToken();
    if (!token) return;

    setLoading(true);
    try {
      let url = `${API_URL}/api/chat/channels/${channelId}/members?limit=10&exclude_self=true`;
      if (query) {
        url += `&q=${encodeURIComponent(query)}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMembers(data.members || []);
        setSelectedIndex(0);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  // Fetch members when search query changes
  useEffect(() => {
    if (visible) {
      fetchMembers(searchQuery);
    }
  }, [visible, searchQuery, fetchMembers]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, members.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          if (members[selectedIndex]) {
            onSelect(members[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, members, selectedIndex, onSelect, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (containerRef.current && members.length > 0) {
      const selectedElement = containerRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, members.length]);

  if (!visible) return null;

  // Check if avatar URL is valid (not empty, not placeholder, starts with http)
  const isValidAvatarUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const trimmed = url.trim();
    if (!trimmed || trimmed === '0' || trimmed === 'null' || trimmed === 'undefined') return false;
    return trimmed.startsWith('http://') || trimmed.startsWith('https://');
  };

  // Get avatar color based on user type
  const getAvatarColor = (userType) => {
    switch (userType) {
      case 'student': return 'bg-blue-500';
      case 'recruiter': return 'bg-green-500';
      case 'alumni': return 'bg-purple-500';
      case 'admin': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get role badge
  const getRoleBadge = (userType) => {
    const badges = {
      recruiter: { text: 'Recruiter', class: 'bg-green-100 text-green-700' },
      alumni: { text: 'Alumni', class: 'bg-purple-100 text-purple-700' },
      admin: { text: 'Admin', class: 'bg-red-100 text-red-700' }
    };
    return badges[userType] || null;
  };

  return (
    <div
      ref={containerRef}
      className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
      style={{
        bottom: position?.bottom || 'auto',
        left: position?.left || 0,
        minWidth: '220px',
        maxWidth: '300px'
      }}
    >
      {/* Header */}
      <div className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-50 border-b border-gray-100 sticky top-0">
        Members
      </div>

      {/* Loading state */}
      {loading && members.length === 0 && (
        <div className="px-3 py-4 text-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      )}

      {/* No results */}
      {!loading && members.length === 0 && (
        <div className="px-3 py-3 text-sm text-gray-500 text-center">
          No members found
        </div>
      )}

      {/* Member list */}
      {members.map((member, index) => {
        const badge = getRoleBadge(member.user_type);
        return (
          <button
            key={`${member.user_type}-${member.user_id}`}
            data-index={index}
            onClick={() => onSelect(member)}
            className={`
              w-full px-3 py-2 flex items-center space-x-2 text-left transition-colors
              ${index === selectedIndex ? 'bg-blue-50' : 'hover:bg-gray-50'}
            `}
          >
            {/* Avatar */}
            {isValidAvatarUrl(member.avatar_url) ? (
              <img
                src={member.avatar_url}
                alt={member.display_name}
                className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                onError={(e) => {
                  // On error, hide image and show initials fallback
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className={`w-7 h-7 rounded-full ${getAvatarColor(member.user_type)} flex items-center justify-center text-white text-xs font-medium flex-shrink-0`}
              style={{ display: isValidAvatarUrl(member.avatar_url) ? 'none' : 'flex' }}
            >
              {getInitials(member.display_name)}
            </div>

            {/* Name and role */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-1">
                <span className="text-sm font-medium text-gray-900 truncate">
                  {member.display_name}
                </span>
                {badge && (
                  <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${badge.class}`}>
                    {badge.text}
                  </span>
                )}
              </div>
            </div>

            {/* Selection indicator */}
            {index === selectedIndex && (
              <span className="text-xs text-gray-400">Enter</span>
            )}
          </button>
        );
      })}

      {/* Hint */}
      <div className="px-3 py-1.5 text-[10px] text-gray-400 bg-gray-50 border-t border-gray-100">
        <span className="mr-2">↑↓ Navigate</span>
        <span className="mr-2">↵ Select</span>
        <span>Esc Cancel</span>
      </div>
    </div>
  );
};

export default MentionAutocomplete;
